import axiosInstance from '../apiServices/axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { DELETE_BULK_TASK, GET_DOCUMENT_NDA, TASK_INTERFACE, TASK_INTERFACE_COMMENTS, TASK_ATTACHMENTS, TASK_LINKED_RECORDS, SHARE_UPDATE_GENERATE, SHARE_UPDATE_EMAIL } from '../apiServices/api_routes';
import { toast } from 'react-toastify';
import { UpdateProjectProgress } from './projectDataSlice';
import {
    buildProjectCsvExportFilename,
    buildProjectCsvTemplateFilename,
} from '../utils/exportPdfFilename';

// Normalize task objects so they always expose a `tags` array field
// and a valid status for Task Management UI.
// - If the backend already returns `tags` (array of strings), we trust it.
// - Otherwise we derive it from the legacy `tag_repr` string.
const normalizeTasksWithTags = tasks => {
    if (!tasks) return [];
    return tasks.map(task => {
        const rawStatus = (task?.status || '').toString().trim();
        const normalizedStatus =
            !rawStatus || rawStatus === 'NOT_APPLICABLE' || rawStatus.toLowerCase() === 'not applicable' || rawStatus.toLowerCase() === 'null'
                ? 'NOT_STARTED'
                : rawStatus;

        const existingTags = Array.isArray(task?.tags)
            ? task.tags.filter(tag => !!tag && typeof tag === 'string')
            : task?.tag_repr
            ? [task.tag_repr]
            : [];

        return {
            ...task,
            tags: existingTags,
            status: normalizedStatus,
            mode: task?.mode || 'FS',
        };
    });
};

export const GetDocumentNDA = createAsyncThunk('GET_DOCUMENT_NDA', async id => {
    const data = await axiosInstance.get(GET_DOCUMENT_NDA + id);
    return data;
});
export const PostTask = createAsyncThunk('POST_TASK_INTERFACE', async (arg, { rejectWithValue, dispatch }) => {
    try {
        const { recalculateDownstream = false, ...body } = arg || {};
        const payload = { ...body };
        if (recalculateDownstream) {
            payload.recalculate_downstream = true;
        }
        const data = await axiosInstance.post(TASK_INTERFACE + localStorage['project_id'] + '/', payload);
        dispatch(GetTask());
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});
export const PatchTask = createAsyncThunk('PATCH_TASK_INTERFACE', async ({ body, id, suppressToast }, { rejectWithValue, dispatch }) => {
    try {
        const data = await axiosInstance.patch(TASK_INTERFACE + localStorage['project_id'] + '/' + id + '/', body);

        // Update project progress after successful task update
        dispatch(UpdateProjectProgress());

        // Scheduling recalculates parent/sibling dates server-side; refresh the full list
        // so the table doesn't keep stale parent duration/timeline after a leaf edit.
        dispatch(GetTask());

        return { ...data, suppressToast };
    } catch (error) {
        dispatch(GetTask());
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const BulkUpdateTasks = createAsyncThunk('BULK_UPDATE_TASKS', async ({ taskIds, updates, suppressToast }, { rejectWithValue, dispatch }) => {
    try {
        const data = await axiosInstance.patch(TASK_INTERFACE + localStorage['project_id'] + '/bulk_update/', {
            task_ids: taskIds,
            updates: updates,
        });

        // Update project progress after successful bulk update
        dispatch(UpdateProjectProgress());
        // Phase 1: schedule cascade updates parent/sibling dates server-side — refresh list
        dispatch(GetTask());

        return { ...data, suppressToast, taskIds, updates };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const GenerateShareUpdate = createAsyncThunk(
    'GENERATE_SHARE_UPDATE',
    async ({ taskIds, purpose }, { rejectWithValue }) => {
        try {
            const projectId = localStorage['project_id'];
            const response = await axiosInstance.post(SHARE_UPDATE_GENERATE(projectId), {
                task_ids: taskIds,
                purpose: purpose || '',
            });
            return response.data;
        } catch (error) {
            const detail = error.response?.data?.error || error.response?.data?.detail || error.message;
            return rejectWithValue(typeof detail === 'string' ? detail : 'Failed to generate update');
        }
    },
);

export const EmailShareUpdate = createAsyncThunk(
    'EMAIL_SHARE_UPDATE',
    async ({ subject, body }, { rejectWithValue }) => {
        try {
            const projectId = localStorage['project_id'];
            const response = await axiosInstance.post(SHARE_UPDATE_EMAIL(projectId), {
                subject,
                body,
            });
            return response.data;
        } catch (error) {
            const detail = error.response?.data?.error || error.response?.data?.detail || error.message;
            return rejectWithValue(typeof detail === 'string' ? detail : 'Failed to send email');
        }
    },
);
export const DeleteTask = createAsyncThunk('DELETE_TASK_INTERFACE', async ({ id, recalculateDownstream = false }, { dispatch }) => {
    const data = await axiosInstance.delete(TASK_INTERFACE + localStorage['project_id'] + '/' + id + '/', {
        data: { recalculate_downstream: recalculateDownstream },
    });

    dispatch(UpdateProjectProgress());
    dispatch(GetTask());

    return data;
});
export const DeleteBulkTask = createAsyncThunk('DELETE_BULK_TASK', async (body, { dispatch }) => {
    const data = await axiosInstance.delete(DELETE_BULK_TASK + localStorage['project_id'] + '/', { data: body });
    dispatch(GetTask());
    return data;
});
export const UpdateBulkTask = createAsyncThunk('UPDATE_BULK_TASK', async (body, obj) => {
    const data = await axiosInstance.put(DELETE_BULK_TASK + localStorage['project_id'] + '/', body);
    return data;
});
export const GetTask = createAsyncThunk('GET_TASK_INTERFACE', async () => {
    const data = await axiosInstance.get(TASK_INTERFACE + localStorage['project_id'] + '/');
    return data;
});
export const PostTaskComment = createAsyncThunk('POST_TASK_INTERFACE_COMMENTS', async ({ body, files }, { rejectWithValue }) => {
    try {
        const projectId = localStorage['project_id'];
        const url = TASK_INTERFACE_COMMENTS + body?.task + '/' + projectId + '/';

        if (files && files.length > 0) {
            const formData = new FormData();
            formData.append('task', body.task);
            if (body.comment) {
                formData.append('comment', body.comment);
            }
            if (body.created_by) {
                formData.append('created_by', body.created_by);
            }
            files.forEach(file => formData.append('files', file));
            const data = await axiosInstance.post(url, formData);
            return data;
        }

        const data = await axiosInstance.post(url, body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});
export const GetTaskComment = createAsyncThunk('GET_TASK_INTERFACE_COMMENTS', async ({ taskId }) => {
    const data = await axiosInstance.get(TASK_INTERFACE_COMMENTS + taskId + '/' + localStorage['project_id'] + '/');
    return data;
});
export const ReorderTasks = createAsyncThunk('REORDER_TASKS', async ({ taskOrders, recalculateDownstream = false }, { rejectWithValue, dispatch }) => {
    try {
        const data = await axiosInstance.post(TASK_INTERFACE + localStorage['project_id'] + '/reorder/', {
            task_orders: taskOrders,
            recalculate_downstream: recalculateDownstream,
        });
        await dispatch(GetTask());
        return data;
    } catch (error) {
        await dispatch(GetTask());
        return rejectWithValue(error.response?.data || error.message);
    }
});
export const DeleteTaskComment = createAsyncThunk('DELETE_TASK_INTERFACE_COMMENTS', async ({ commentId, taskId }) => {
    const data = await axiosInstance.delete(TASK_INTERFACE_COMMENTS + taskId + '/' + localStorage['project_id'] + '/' + commentId + '/');
    return data;
});
export const PatchTaskComment = createAsyncThunk('PATCH_TASK_INTERFACE_COMMENTS', async ({ commentId, body }, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.patch(`/tools/task-comments/${commentId}/`, body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const GetTaskAttachments = createAsyncThunk('GET_TASK_ATTACHMENTS', async ({ taskId }, { rejectWithValue }) => {
    try {
        const projectId = localStorage['project_id'];
        const data = await axiosInstance.get(`${TASK_ATTACHMENTS}${projectId}/`, {
            params: { task_id: taskId },
        });
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const UploadTaskAttachment = createAsyncThunk('UPLOAD_TASK_ATTACHMENT', async ({ taskId, files }, { rejectWithValue }) => {
    try {
        const projectId = localStorage['project_id'];
        const formData = new FormData();
        formData.append('task_id', taskId);
        files.forEach(file => formData.append('files', file));
        const data = await axiosInstance.post(`${TASK_ATTACHMENTS}${projectId}/`, formData);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const UploadCommentAttachments = createAsyncThunk('UPLOAD_COMMENT_ATTACHMENTS', async ({ commentId, files }, { rejectWithValue }) => {
    try {
        const projectId = localStorage['project_id'];
        const formData = new FormData();
        formData.append('comment_id', commentId);
        files.forEach(file => formData.append('files', file));
        const data = await axiosInstance.post(`${TASK_ATTACHMENTS}${projectId}/`, formData);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DeleteTaskAttachment = createAsyncThunk('DELETE_TASK_ATTACHMENT', async ({ attachmentId }, { rejectWithValue }) => {
    try {
        const projectId = localStorage['project_id'];
        const data = await axiosInstance.delete(`${TASK_ATTACHMENTS}${projectId}/${attachmentId}/`);
        return { ...data, attachmentId };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DownloadTaskAttachment = createAsyncThunk('DOWNLOAD_TASK_ATTACHMENT', async ({ attachmentId }, { rejectWithValue }) => {
    try {
        const projectId = localStorage['project_id'];
        const response = await axiosInstance.get(`${TASK_ATTACHMENTS}${projectId}/${attachmentId}/download/`);
        return response.data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const GetTaskLinkedRecords = createAsyncThunk('GET_TASK_LINKED_RECORDS', async ({ taskId }, { rejectWithValue }) => {
    try {
        const projectId = localStorage['project_id'];
        const data = await axiosInstance.get(`${TASK_LINKED_RECORDS}${projectId}/`, {
            params: { task_id: taskId },
        });
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const CreateTaskLinkedRecord = createAsyncThunk('CREATE_TASK_LINKED_RECORD', async (body, { rejectWithValue }) => {
    try {
        const projectId = localStorage['project_id'];
        const data = await axiosInstance.post(`${TASK_LINKED_RECORDS}${projectId}/`, body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DeleteTaskLinkedRecord = createAsyncThunk('DELETE_TASK_LINKED_RECORD', async ({ linkId }, { rejectWithValue }) => {
    try {
        const projectId = localStorage['project_id'];
        await axiosInstance.delete(`${TASK_LINKED_RECORDS}${projectId}/${linkId}/`);
        return { linkId };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const UploadCSVFile = createAsyncThunk('TASK_INTERFACE_UPLOAD_CSV_FILE', async (body, { getState, rejectWithValue }) => {
    try {
        console.log('Making API call to:', TASK_INTERFACE + localStorage['project_id'] + '/bulk_upload_csv/');
        const data = await axiosInstance.post(TASK_INTERFACE + localStorage['project_id'] + '/bulk_upload_csv/', body, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return data;
    } catch (error) {
        console.log('Upload error:', error);
        return rejectWithValue(error.response?.data || 'Upload failed');
    }
});

export const DownloadCSVTemplate = createAsyncThunk('TASK_INTERFACE_DOWNLOAD_CSV_TEMPLATE', async (_, { rejectWithValue, getState }) => {
    try {
        const response = await axiosInstance.get(TASK_INTERFACE + localStorage['project_id'] + '/download_template/', {
            responseType: 'blob',
        });
        return {
            data: response.data,
            filename: buildProjectCsvTemplateFilename(getState, 'Tasks'),
        };
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Download failed');
    }
});

export const ExportTasksCSV = createAsyncThunk('TASK_INTERFACE_EXPORT_CSV', async (taskIds = null, { rejectWithValue, getState }) => {
    try {
        let url = TASK_INTERFACE + localStorage['project_id'] + '/export_csv/';
        if (taskIds && taskIds.length > 0) {
            // Add task_ids as query parameter
            const taskIdsParam = taskIds.join(',');
            url += `?task_ids=${taskIdsParam}`;
        }
        const response = await axiosInstance.get(url, {
            responseType: 'blob',
        });
        return {
            data: response.data,
            filename: buildProjectCsvExportFilename(getState, { label: 'Tasks', ids: taskIds }),
        };
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Export failed');
    }
});

export const ImportTechDDTasks = createAsyncThunk('TASK_INTERFACE_IMPORT_TECH_DD_TASKS', async (_, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.post(TASK_INTERFACE + localStorage['project_id'] + '/import_tech_dd_tasks/');
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Import failed');
    }
});

// Project Task Workstreams
export const GetProjectTaskWorkstreams = createAsyncThunk('GET_PROJECT_TASK_WORKSTREAMS', async () => {
    const data = await axiosInstance.get('/tools/project-task-workstreams/' + localStorage['project_id'] + '/');
    return data;
});

export const PostProjectTaskWorkstream = createAsyncThunk('POST_PROJECT_TASK_WORKSTREAM', async (body, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.post('/tools/project-task-workstreams/' + localStorage['project_id'] + '/', body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PatchProjectTaskWorkstream = createAsyncThunk('PATCH_PROJECT_TASK_WORKSTREAM', async ({ id, body }, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.patch(`/tools/project-task-workstreams/${id}/`, body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DeleteProjectTaskWorkstream = createAsyncThunk('DELETE_PROJECT_TASK_WORKSTREAM', async (id, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.delete(`/tools/project-task-workstreams/${id}/`);
        return { id, data };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

// Project Task Tags
export const GetProjectTaskTags = createAsyncThunk('GET_PROJECT_TASK_TAGS', async () => {
    const data = await axiosInstance.get('/tools/project-task-tags/' + localStorage['project_id'] + '/');
    return data;
});

export const PostProjectTaskTag = createAsyncThunk('POST_PROJECT_TASK_TAG', async (body, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.post('/tools/project-task-tags/' + localStorage['project_id'] + '/', body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PatchProjectTaskTag = createAsyncThunk('PATCH_PROJECT_TASK_TAG', async ({ id, body }, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.patch(`/tools/project-task-tags/${localStorage['project_id']}/${id}/`, body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DeleteProjectTaskTag = createAsyncThunk('DELETE_PROJECT_TASK_TAG', async (id, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.delete(`/tools/project-task-tags/${localStorage['project_id']}/${id}/`);
        return { id, data };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

// Project Task Phases
export const GetProjectTaskPhases = createAsyncThunk('GET_PROJECT_TASK_PHASES', async () => {
    const data = await axiosInstance.get('/tools/project-task-phases/' + localStorage['project_id'] + '/');
    return data;
});

export const PostProjectTaskPhase = createAsyncThunk('POST_PROJECT_TASK_PHASE', async (body, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.post('/tools/project-task-phases/' + localStorage['project_id'] + '/', body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PatchProjectTaskPhase = createAsyncThunk('PATCH_PROJECT_TASK_PHASE', async ({ id, body }, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.patch(`/tools/project-task-phases/${localStorage['project_id']}/${id}/`, body);
        return data;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DeleteProjectTaskPhase = createAsyncThunk('DELETE_PROJECT_TASK_PHASE', async (id, { rejectWithValue }) => {
    try {
        const data = await axiosInstance.delete(`/tools/project-task-phases/${localStorage['project_id']}/${id}/`);
        return { id, data };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const taskSlice = createSlice({
    name: 'taskSlice',
    initialState: {
        taskData: [],
        taskComments: [],
        taskAttachments: [],
        taskAttachmentLoading: false,
        taskLinkedRecords: [],
        taskLinkedRecordsLoading: false,
        projectWorkstreams: [],
        projectTags: [],
        projectPhases: [],
        taskFilters: null,
        taskButtonLoading: false,
        taskCommentBtnLoading: false,
        uploadCSVBtnLoading: false,
        downloadTemplateLoading: false,
        exportCSVLoading: false,
        shareUpdateGenerateLoading: false,
        shareUpdateEmailLoading: false,
        groupBySettings: {
            column: 'project_phase',
            sort: '',
            collapsedGroups: [],
        },
    },
    reducers: {
        resetTaskData: () => {
            return {
                taskData: [],
                taskComments: [],
                taskAttachments: [],
                taskAttachmentLoading: false,
                taskLinkedRecords: [],
                taskLinkedRecordsLoading: false,
                projectWorkstreams: [],
                projectTags: [],
                projectPhases: [],
                taskFilters: null,
                taskButtonLoading: false,
                taskCommentBtnLoading: false,
                uploadCSVBtnLoading: false,
                downloadTemplateLoading: false,
                groupBySettings: {
                    column: 'project_phase',
                    sort: '',
                    collapsedGroups: [],
                },
            };
        },
        setGroupByColumn: (state, { payload }) => {
            state.groupBySettings.column = payload;
        },
        setGroupBySort: (state, { payload }) => {
            state.groupBySettings.sort = payload;
        },
        setCollapsedGroups: (state, { payload }) => {
            state.groupBySettings.collapsedGroups = payload;
        },
        clearGroupBySettings: state => {
            state.groupBySettings = {
                column: '',
                sort: '',
                collapsedGroups: [],
            };
        },
        resetGroupBySettings: state => {
            state.groupBySettings = {
                column: 'project_phase',
                sort: '',
                collapsedGroups: [],
            };
        },
        loadGroupBySettings: (state, { payload }) => {
            if (payload) {
                state.groupBySettings = {
                    column: payload.column || '',
                    sort: payload.sort || '',
                    collapsedGroups: payload.collapsedGroups || [],
                };
            }
        },
        applyTaskOrderUpdates: (state, { payload: taskOrders }) => {
            if (!taskOrders?.length || !state.taskData?.length) {
                return;
            }

            const updatesById = new Map(taskOrders.map(item => [item.id, item]));
            state.taskData = state.taskData.map(task => {
                const update = updatesById.get(task.id);
                if (!update) {
                    return task;
                }

                return {
                    ...task,
                    order: update.order,
                    parent_task: Object.prototype.hasOwnProperty.call(update, 'parent_task')
                        ? update.parent_task
                        : task.parent_task,
                    level: Object.prototype.hasOwnProperty.call(update, 'level') ? update.level : task.level,
                };
            });
        },
    },
    extraReducers: {
        //GetTask
        [GetTask.pending]: (state, action) => {
            state.status = 'loading';
        },
        [GetTask.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            const rawTasks = payload?.data?.tasks || [];
            state.taskData = normalizeTasksWithTags(rawTasks);
            let obj = payload?.data?.filters;
            obj.priority = ['HIGH', 'MEDIUM', 'LOW'];
            state.taskFilters = obj;
            // state.signeesData = action.payload?.data;
        },
        [GetTask.rejected]: (state, action) => {
            state.status = 'rejected';
        },
        //PostTask
        [PostTask.pending]: (state, action) => {
            state.status = 'loading';
            state.taskButtonLoading = true;
        },
        [PostTask.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            toast.success('Task created successfully');
            state.taskButtonLoading = false;
            if (payload?.data) {
                const [normalizedTask] = normalizeTasksWithTags([payload.data]);
                const parentId = normalizedTask?.parent_task;
                if (parentId && state.taskData) {
                    // Parent schedule fields are cleared server-side when the first subtask is added.
                    state.taskData = state.taskData.map(item =>
                        item?.id === parentId
                            ? {
                                  ...item,
                                  start_date: null,
                                  end_date: null,
                                  duration: null,
                                  deadline: null,
                              }
                            : item,
                    );
                }
                state.taskData = state.taskData ? [...state.taskData, normalizedTask] : [normalizedTask];
            }
        },
        [PostTask.rejected]: (state, action) => {
            Object.keys(action.payload).map(key => action.payload[key].map(item => toast.error(key.toUpperCase() + ' : ' + item)));
            state.status = 'rejected';
            state.taskButtonLoading = false;
        },
        //PatchTask
        [PatchTask.pending]: (state, action) => {
            state.status = 'loading';
            state.taskButtonLoading = true;
        },
        [PatchTask.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            if (!payload?.suppressToast) toast.success('Success');
            state.taskButtonLoading = false;
            state.taskData = state.taskData?.map(item => {
                if (item?.id === payload?.data?.id) {
                    const [normalizedTask] = normalizeTasksWithTags([payload.data]);
                    return normalizedTask;
                } else return item;
            });
        },
        [PatchTask.rejected]: (state, action) => {
            const body = action.meta?.arg?.body || {};
            const scheduleError =
                body.status != null ||
                body.mode != null ||
                body.start_date != null ||
                body.end_date != null ||
                body.duration != null ||
                body.duration_minutes != null;
            toast.error(
                scheduleError
                    ? 'Schedule could not be recalculated. No schedule changes were applied.'
                    : 'Failed',
            );
            state.taskButtonLoading = false;
            state.status = 'rejected';
        },
        //BulkUpdateTasks
        [BulkUpdateTasks.pending]: (state, action) => {
            state.status = 'loading';
            state.taskButtonLoading = true;
        },
        [BulkUpdateTasks.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            if (!payload?.suppressToast) toast.success(`Successfully updated ${payload?.data?.updated_count || payload?.taskIds?.length} tasks`);
            state.taskButtonLoading = false;

            // Update the tasks in the state with the new values
            if (payload?.taskIds && payload?.updates) {
                state.taskData = state.taskData?.map(item => {
                    if (payload.taskIds.includes(item?.id)) {
                        return { ...item, ...payload.updates };
                    }
                    return item;
                });
                // Ensure tags are normalized after bulk updates so `tags` and `tag_repr`
                // stay consistent for consumers that rely on the array form.
                state.taskData = normalizeTasksWithTags(state.taskData);
            }
        },
        [BulkUpdateTasks.rejected]: (state, action) => {
            toast.error('Failed to update tasks');
            state.taskButtonLoading = false;
            state.status = 'rejected';
        },
        //DeleteTask
        [DeleteTask.pending]: (state, action) => {
            state.status = 'loading';
        },
        [DeleteTask.fulfilled]: (state, { meta }) => {
            state.status = 'success';
            toast.success('Success');
            state.taskData = state.taskData?.filter(item => item?.id !== meta?.arg?.id);
        },
        [DeleteTask.rejected]: (state, action) => {
            toast.error('Failed');
            state.status = 'rejected';
        },
        //GetTaskComment
        [GetTaskComment.pending]: (state, action) => {
            state.status = 'loading';
        },
        [GetTaskComment.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            state.taskComments = payload?.data;
            // state.signeesData = action.payload?.data;
        },
        [GetTaskComment.rejected]: (state, action) => {
            state.status = 'rejected';
        },
        //PostTaskComment
        [PostTaskComment.pending]: (state, action) => {
            state.status = 'loading';
            state.taskCommentBtnLoading = true;
        },
        [PostTaskComment.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            toast.success('Success');
            state.taskComments.push(payload?.data);

            // Update the task's comments_count in taskData
            const taskId = payload?.data?.task;
            if (taskId) {
                state.taskData = state.taskData.map(task => {
                    if (task.id === taskId) {
                        return {
                            ...task,
                            comments_count: (task.comments_count || 0) + 1,
                        };
                    }
                    return task;
                });
            }

            state.taskCommentBtnLoading = false;
        },
        [PostTaskComment.rejected]: (state, action) => {
            toast.error('Failed');
            state.status = 'rejected';
            state.taskCommentBtnLoading = false;
        },
        //DeleteTaskComment
        [DeleteTaskComment.pending]: (state, action) => {
            state.status = 'loading';
            state.taskCommentBtnLoading = true;
        },
        [DeleteTaskComment.fulfilled]: (state, { payload, meta }) => {
            state.status = 'success';
            toast.success('Comment deleted');

            // Remove comment from taskComments
            const commentId = meta?.arg?.commentId;
            if (commentId) {
                state.taskComments = state.taskComments.filter(comment => comment.id !== commentId);
            }

            // Update the task's comments_count in taskData
            const taskId = meta?.arg?.taskId;
            if (taskId) {
                state.taskData = state.taskData.map(task => {
                    if (task.id === taskId) {
                        return {
                            ...task,
                            comments_count: Math.max(0, (task.comments_count || 0) - 1),
                        };
                    }
                    return task;
                });
            }

            state.taskCommentBtnLoading = false;
        },
        [DeleteTaskComment.rejected]: (state, action) => {
            toast.error('Failed to delete comment');
            state.status = 'rejected';
            state.taskCommentBtnLoading = false;
        },
        //PatchTaskComment
        [PatchTaskComment.pending]: (state, action) => {
            state.status = 'loading';
            state.taskCommentBtnLoading = true;
        },
        [PatchTaskComment.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            toast.success('Comment updated successfully');

            // Update the comment in taskComments
            if (payload?.data) {
                state.taskComments = state.taskComments.map(comment => {
                    if (comment.id === payload.data.id) {
                        return payload.data;
                    }
                    return comment;
                });
            }

            state.taskCommentBtnLoading = false;
        },
        [PatchTaskComment.rejected]: (state, action) => {
            toast.error('Failed to update comment');
            state.status = 'rejected';
            state.taskCommentBtnLoading = false;
        },
        [GetTaskAttachments.pending]: state => {
            state.taskAttachmentLoading = true;
        },
        [GetTaskAttachments.fulfilled]: (state, { payload }) => {
            state.taskAttachmentLoading = false;
            state.taskAttachments = payload?.data || [];
        },
        [GetTaskAttachments.rejected]: state => {
            state.taskAttachmentLoading = false;
        },
        [UploadTaskAttachment.pending]: state => {
            state.taskAttachmentLoading = true;
        },
        [UploadTaskAttachment.fulfilled]: (state, { payload, meta }) => {
            state.taskAttachmentLoading = false;
            const uploaded = Array.isArray(payload?.data) ? payload.data : [payload?.data].filter(Boolean);
            state.taskAttachments = [...state.taskAttachments, ...uploaded];
            const taskId = meta?.arg?.taskId;
            if (taskId) {
                state.taskData = state.taskData?.map(item =>
                    item?.id === taskId
                        ? { ...item, attachments_count: (item.attachments_count ?? 0) + uploaded.length }
                        : item,
                );
            }
            toast.success('Attachment uploaded');
        },
        [UploadTaskAttachment.rejected]: (state, action) => {
            state.taskAttachmentLoading = false;
            toast.error(action.payload?.error || 'Failed to upload attachment');
        },
        [UploadCommentAttachments.fulfilled]: (state, { payload, meta }) => {
            const uploaded = Array.isArray(payload?.data) ? payload.data : [payload?.data].filter(Boolean);
            const commentId = meta?.arg?.commentId;
            if (commentId && uploaded.length) {
                state.taskComments = state.taskComments.map(comment => {
                    if (comment.id === commentId) {
                        return {
                            ...comment,
                            attachments: [...(comment.attachments || []), ...uploaded],
                        };
                    }
                    return comment;
                });
            }
            toast.success('Attachment uploaded');
        },
        [UploadCommentAttachments.rejected]: (state, action) => {
            toast.error(action.payload?.error || 'Failed to upload attachment');
        },
        [DeleteTaskAttachment.fulfilled]: (state, { meta }) => {
            const attachmentId = meta?.arg?.attachmentId;
            const removed = state.taskAttachments.find(a => a.id === attachmentId);
            state.taskAttachments = state.taskAttachments.filter(a => a.id !== attachmentId);
            state.taskComments = state.taskComments.map(comment => ({
                ...comment,
                attachments: (comment.attachments || []).filter(a => a.id !== attachmentId),
            }));
            const taskId = removed?.task;
            if (taskId) {
                state.taskData = state.taskData?.map(item =>
                    item?.id === taskId
                        ? { ...item, attachments_count: Math.max(0, (item.attachments_count ?? 0) - 1) }
                        : item,
                );
            }
            toast.success('Attachment deleted');
        },
        [DeleteTaskAttachment.rejected]: (state, action) => {
            toast.error(action.payload?.error || 'Failed to delete attachment');
        },
        [GetTaskLinkedRecords.pending]: state => {
            state.taskLinkedRecordsLoading = true;
        },
        [GetTaskLinkedRecords.fulfilled]: (state, { payload }) => {
            state.taskLinkedRecordsLoading = false;
            state.taskLinkedRecords = payload?.data || [];
        },
        [GetTaskLinkedRecords.rejected]: state => {
            state.taskLinkedRecordsLoading = false;
            state.taskLinkedRecords = [];
        },
        [CreateTaskLinkedRecord.fulfilled]: (state, { payload }) => {
            if (payload?.data) {
                state.taskLinkedRecords = [...state.taskLinkedRecords, payload.data];
                const taskId = payload.data.task;
                if (taskId) {
                    state.taskData = state.taskData?.map(item =>
                        item?.id === taskId
                            ? { ...item, linked_records_count: (item.linked_records_count ?? 0) + 1 }
                            : item,
                    );
                }
            }
            toast.success('Reference added');
        },
        [CreateTaskLinkedRecord.rejected]: (state, action) => {
            const err = action.payload;
            const message =
                err?.error ||
                err?.document?.[0] ||
                err?.information_request?.[0] ||
                err?.observation?.[0] ||
                (typeof err === 'string' ? err : null) ||
                'Failed to add reference';
            toast.error(message);
        },
        [DeleteTaskLinkedRecord.fulfilled]: (state, { payload }) => {
            const removed = state.taskLinkedRecords.find(link => link.id === payload?.linkId);
            state.taskLinkedRecords = state.taskLinkedRecords.filter(link => link.id !== payload?.linkId);
            const taskId = removed?.task;
            if (taskId) {
                state.taskData = state.taskData?.map(item =>
                    item?.id === taskId
                        ? { ...item, linked_records_count: Math.max(0, (item.linked_records_count ?? 0) - 1) }
                        : item,
                );
            }
            toast.success('Reference removed');
        },
        [DeleteTaskLinkedRecord.rejected]: (state, action) => {
            toast.error(action.payload?.error || 'Failed to remove reference');
        },

        //DeleteBulkTask
        [DeleteBulkTask.fulfilled]: (state, action) => {
            const ids = action?.meta?.arg?.ids;
            state.taskData = state.taskData.filter(item => {
                let check = ids.find(id => id === item?.id);
                if (!check) return item;
            });
            state.status = 'success';
            // Don't show toast here as it's handled in the component
        },
        [DeleteBulkTask.rejected]: (state, action) => {
            state.status = 'rejected';
            toast.error('Error');
        },
        //UpdateBulkTask
        [UpdateBulkTask.fulfilled]: (state, { payload, meta }) => {
            console.log(payload);
            let newData = state.taskData;
            Object.keys(payload?.data).map(id => {
                newData = newData.map(item => {
                    if (item?.id == id) {
                        let obj = {};
                        Object.keys(payload?.data[id]).map(key => {
                            if (key === 'scope_id' || key === 'assigned_to_id') {
                                obj[key.replace('_id', '')] = payload?.data[id][key];
                            } else {
                                obj[key] = payload?.data[id][key];
                            }
                        });
                        console.log(obj);
                        return { ...item, ...obj };
                    } else return item;
                });
            });
            state.taskData = newData;
            state.status = 'rejected';
            toast.success('Success');
        },
        [UpdateBulkTask.rejected]: (state, action) => {
            state.status = 'rejected';
            toast.error('Error');
        },
        [UploadCSVFile.pending]: state => {
            state.uploadCSVBtnLoading = true;
        },
        [UploadCSVFile.fulfilled]: (state, action) => {
            state.uploadCSVBtnLoading = false;
            // Update task data with the response
            if (action.payload?.data?.tasks) {
                const normalizedNewTasks = normalizeTasksWithTags(action.payload.data.tasks);
                state.taskData = normalizeTasksWithTags([...state.taskData, ...normalizedNewTasks]);
                // Update filters if they exist in the response
                if (action.payload.data.filters) {
                    let obj = action.payload.data.filters;
                    obj.priority = ['HIGH', 'MEDIUM', 'LOW'];
                    state.taskFilters = obj;
                }
            }

            // Show success message with skipped rows information
            const created = action.payload?.data?.created || 0;
            const skipped = action.payload?.data?.skipped || 0;
            const skippedRows = action.payload?.data?.skipped_rows || [];

            if (skipped > 0) {
                const skippedRowNumbers = skippedRows.map(row => row.row).join(', ');
                const message = `Success! ${created} task${created !== 1 ? 's' : ''} created. ${skipped} skipped — special characters found in Row ${skippedRowNumbers}.`;
                toast.warning(message, {
                    autoClose: 5000, // 5 seconds
                    hideProgressBar: false,
                });
            } else {
                toast.success(`Success! ${created} task${created !== 1 ? 's' : ''} created.`, {
                    autoClose: 3000, // 3 seconds for success without skipped rows
                    hideProgressBar: false,
                });
            }
        },
        [UploadCSVFile.rejected]: (state, action) => {
            state.uploadCSVBtnLoading = false;
            toast.error(action.payload || 'Upload failed');
        },
        [DownloadCSVTemplate.pending]: state => {
            state.downloadTemplateLoading = true;
        },
        [DownloadCSVTemplate.fulfilled]: (state, action) => {
            state.downloadTemplateLoading = false;
            const url = window.URL.createObjectURL(new Blob([action.payload.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', action.payload.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Template downloaded successfully');
        },
        [DownloadCSVTemplate.rejected]: (state, action) => {
            state.downloadTemplateLoading = false;
            toast.error(action.payload || 'Download failed');
        },
        [ExportTasksCSV.pending]: state => {
            state.exportCSVLoading = true;
        },
        [ExportTasksCSV.fulfilled]: (state, action) => {
            state.exportCSVLoading = false;
            const taskIds = action.meta?.arg;
            const successMessage = taskIds && taskIds.length > 0 ? `Successfully exported ${taskIds.length} task${taskIds.length > 1 ? 's' : ''}` : 'Tasks exported successfully';

            const url = window.URL.createObjectURL(new Blob([action.payload.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', action.payload.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(successMessage);
        },
        [ExportTasksCSV.rejected]: (state, action) => {
            state.exportCSVLoading = false;
            toast.error(action.payload || 'Failed to export tasks');
        },
        [GenerateShareUpdate.pending]: state => {
            state.shareUpdateGenerateLoading = true;
        },
        [GenerateShareUpdate.fulfilled]: state => {
            state.shareUpdateGenerateLoading = false;
        },
        [GenerateShareUpdate.rejected]: state => {
            state.shareUpdateGenerateLoading = false;
        },
        [EmailShareUpdate.pending]: state => {
            state.shareUpdateEmailLoading = true;
        },
        [EmailShareUpdate.fulfilled]: state => {
            state.shareUpdateEmailLoading = false;
            toast.success('Email sent to your inbox');
        },
        [EmailShareUpdate.rejected]: (state, action) => {
            state.shareUpdateEmailLoading = false;
            toast.error(action.payload || 'Failed to send email');
        },
        [ImportTechDDTasks.pending]: state => {
            state.uploadCSVBtnLoading = true;
        },
        [ImportTechDDTasks.fulfilled]: (state, action) => {
            state.uploadCSVBtnLoading = false;
            // Update task data with the response
            if (action.payload?.data?.tasks) {
                const normalizedNewTasks = normalizeTasksWithTags(action.payload.data.tasks);
                state.taskData = normalizeTasksWithTags([...state.taskData, ...normalizedNewTasks]);
                // Update filters if they exist in the response
                if (action.payload.data.filters) {
                    let obj = action.payload.data.filters;
                    obj.priority = ['HIGH', 'MEDIUM', 'LOW'];
                    state.taskFilters = obj;
                }
            }

            // Show success message with skipped rows information
            const created = action.payload?.data?.created || 0;
            const skipped = action.payload?.data?.skipped || 0;
            const skippedRows = action.payload?.data?.skipped_rows || [];

            if (skipped > 0) {
                const skippedRowNumbers = skippedRows.map(row => row.row).join(', ');
                const message = `Success! ${created} Tech DD task${created !== 1 ? 's' : ''} imported. ${skipped} skipped — Row ${skippedRowNumbers}.`;
                toast.warning(message, {
                    autoClose: 5000, // 5 seconds
                    hideProgressBar: false,
                });
            } else {
                toast.success(`Success! ${created} Tech DD task${created !== 1 ? 's' : ''} imported.`, {
                    autoClose: 3000, // 3 seconds for success without skipped rows
                    hideProgressBar: false,
                });
            }
        },
        [ImportTechDDTasks.rejected]: (state, action) => {
            state.uploadCSVBtnLoading = false;
            toast.error(action.payload || 'Tech DD import failed');
        },

        // Project Task Workstreams
        [GetProjectTaskWorkstreams.fulfilled]: (state, { payload }) => {
            state.projectWorkstreams = payload?.data || [];
        },
        [GetProjectTaskWorkstreams.rejected]: (state, action) => {
            toast.error('Failed to fetch project workstreams');
        },
        [PostProjectTaskWorkstream.fulfilled]: (state, { payload }) => {
            state.projectWorkstreams.push(payload?.data);
            toast.success('Workstream created successfully');
        },
        [PostProjectTaskWorkstream.rejected]: (state, action) => {
            toast.error('Failed to create workstream');
        },
        [PatchProjectTaskWorkstream.fulfilled]: (state, { payload }) => {
            state.projectWorkstreams = state.projectWorkstreams.map(item => (item.id === payload?.data?.id ? payload?.data : item));
            toast.success('Workstream updated successfully');
        },
        [PatchProjectTaskWorkstream.rejected]: (state, action) => {
            toast.error('Failed to update workstream');
        },
        [DeleteProjectTaskWorkstream.fulfilled]: (state, { payload }) => {
            state.projectWorkstreams = state.projectWorkstreams.filter(item => item.id !== payload?.id);
            toast.success('Workstream deleted successfully');
        },
        [DeleteProjectTaskWorkstream.rejected]: (state, action) => {
            toast.error('Failed to delete workstream');
        },

        // Project Task Tags
        [GetProjectTaskTags.fulfilled]: (state, { payload }) => {
            state.projectTags = payload?.data || [];
        },
        [GetProjectTaskTags.rejected]: (state, action) => {
            toast.error('Failed to fetch project tags');
        },
        [PostProjectTaskTag.fulfilled]: (state, { payload }) => {
            state.projectTags.push(payload?.data);
            toast.success('Tag created successfully');
        },
        [PostProjectTaskTag.rejected]: (state, action) => {
            toast.error('Failed to create tag');
        },
        [PatchProjectTaskTag.fulfilled]: (state, { payload }) => {
            state.projectTags = state.projectTags.map(item => (item.id === payload?.data?.id ? payload?.data : item));
            toast.success('Tag updated successfully');
        },
        [PatchProjectTaskTag.rejected]: (state, action) => {
            toast.error('Failed to update tag');
        },
        [DeleteProjectTaskTag.fulfilled]: (state, { payload }) => {
            state.projectTags = state.projectTags.filter(item => item.id !== payload?.id);
            toast.success('Tag deleted successfully');
        },
        [DeleteProjectTaskTag.rejected]: (state, action) => {
            toast.error('Failed to delete tag');
        },
        // Project Task Phases
        [GetProjectTaskPhases.fulfilled]: (state, { payload }) => {
            state.projectPhases = payload?.data || [];
        },
        [GetProjectTaskPhases.rejected]: (state, action) => {
            toast.error('Failed to fetch project phases');
        },
        [PostProjectTaskPhase.fulfilled]: (state, { payload }) => {
            state.projectPhases.push(payload?.data);
            toast.success('Project phase created successfully');
        },
        [PostProjectTaskPhase.rejected]: (state, action) => {
            toast.error('Failed to create project phase');
        },
        [PatchProjectTaskPhase.fulfilled]: (state, { payload }) => {
            state.projectPhases = state.projectPhases.map(item => (item.id === payload?.data?.id ? payload?.data : item));
            toast.success('Project phase updated successfully');
        },
        [PatchProjectTaskPhase.rejected]: (state, action) => {
            toast.error('Failed to update project phase');
        },
        [DeleteProjectTaskPhase.fulfilled]: (state, { payload }) => {
            state.projectPhases = state.projectPhases.filter(item => item.id !== payload?.id);
            toast.success('Project phase deleted successfully');
        },
        [DeleteProjectTaskPhase.rejected]: (state, action) => {
            toast.error('Failed to delete project phase');
        },
        // ReorderTasks
        [ReorderTasks.pending]: (state, action) => {
            state.status = 'loading';
            const taskOrders = action.meta.arg;
            if (!taskOrders?.length || !state.taskData?.length) {
                return;
            }

            const updatesById = new Map(taskOrders.map(item => [item.id, item]));
            state.taskData = state.taskData.map(task => {
                const update = updatesById.get(task.id);
                if (!update) {
                    return task;
                }

                return {
                    ...task,
                    order: update.order,
                    parent_task: Object.prototype.hasOwnProperty.call(update, 'parent_task')
                        ? update.parent_task
                        : task.parent_task,
                    level: Object.prototype.hasOwnProperty.call(update, 'level') ? update.level : task.level,
                };
            });
        },
        [ReorderTasks.fulfilled]: state => {
            state.status = 'success';
        },
        [ReorderTasks.rejected]: (state, action) => {
            state.status = 'error';
            toast.error('Failed to reorder tasks');
        },
    },
});
export const { resetTaskData, setGroupByColumn, setGroupBySort, setCollapsedGroups, clearGroupBySettings, resetGroupBySettings, loadGroupBySettings } = taskSlice.actions;
export default taskSlice.reducer;
